import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, ChevronDown, Check } from "lucide-react";

const EMIRATES = ["Dubai", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];

// Shared area filter: emirate grouping + search + select-all-shown. Mobile-first (44px targets).
export default function AreaFilter({ garages = [], selected = [], onChange, testId = "area-filter" }) {
  const [q, setQ] = useState("");
  const [emirate, setEmirate] = useState(""); // "" = all emirates

  const { areaCounts, emirateOf, emirateCounts, areaList } = useMemo(() => {
    const ac = {}, eo = {}, ec = {};
    for (const g of garages) {
      const a = g.area;
      if (!a) continue;
      ac[a] = (ac[a] || 0) + 1;
      const e = g.emirate || "Other";
      eo[a] = e;
      ec[e] = (ec[e] || 0) + 1;
    }
    const list = Object.keys(ac).sort((x, y) => ac[y] - ac[x]);
    return { areaCounts: ac, emirateOf: eo, emirateCounts: ec, areaList: list };
  }, [garages]);

  const visible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return areaList.filter(
      (a) => (!emirate || emirateOf[a] === emirate) && (!ql || a.toLowerCase().includes(ql))
    );
  }, [areaList, q, emirate, emirateOf]);

  const toggle = (a) =>
    onChange(selected.includes(a) ? selected.filter((x) => x !== a) : [...selected, a]);
  const allShownSelected = visible.length > 0 && visible.every((a) => selected.includes(a));
  const selectAllShown = () => {
    if (allShownSelected) onChange(selected.filter((a) => !visible.includes(a)));
    else onChange(Array.from(new Set([...selected, ...visible])));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testId}
          className="h-11 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm font-medium flex items-center gap-2 min-w-[140px] justify-between">
          <span className="truncate">{selected.length ? `${selected.length} area${selected.length > 1 ? "s" : ""}` : "All areas"}</span>
          <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,340px)] p-0 overflow-hidden">
        <div className="p-2.5 border-b border-[#F3F4F6]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search areas, e.g. Quoz…" data-testid={`${testId}-search`}
              className="w-full h-11 pl-9 pr-3 rounded-md border border-[#D1D5DB] text-sm focus:outline-none focus:border-[#1E3A8A]" />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <button onClick={() => setEmirate("")} data-testid={`${testId}-emirate-all`}
              className={`h-11 px-3 rounded-full text-xs font-bold border ${!emirate ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#374151] border-[#D1D5DB]"}`}>All</button>
            {EMIRATES.filter((e) => emirateCounts[e]).map((e) => (
              <button key={e} onClick={() => setEmirate(emirate === e ? "" : e)} data-testid={`${testId}-emirate-${e.replace(/\s+/g, "-")}`}
                className={`h-11 px-3 rounded-full text-xs font-bold border ${emirate === e ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#374151] border-[#D1D5DB]"}`}>
                {e} <span className={emirate === e ? "text-white/70" : "text-[#9CA3AF]"}>({emirateCounts[e]})</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between px-2.5 py-2 border-b border-[#F3F4F6]">
          <button onClick={selectAllShown} data-testid={`${testId}-select-all`}
            className="h-9 px-2 text-xs font-bold text-[#1E3A8A] hover:bg-[#EEF2FF] rounded-md flex items-center gap-1">
            <Check className="w-3.5 h-3.5" /> {allShownSelected ? "Unselect shown" : `Select all shown (${visible.length})`}
          </button>
          {selected.length > 0 && (
            <button onClick={() => onChange([])} data-testid={`${testId}-clear`}
              className="h-9 px-2 text-xs font-bold text-[#B91C1C] hover:bg-[#FEF2F2] rounded-md">Clear ({selected.length})</button>
          )}
        </div>
        <div className="max-h-[42vh] overflow-y-auto p-1">
          {visible.length === 0 && <p className="text-sm text-[#9CA3AF] px-2 py-4 text-center">No areas match</p>}
          {visible.map((a) => (
            <label key={a} data-testid={`area-opt-${a}`}
              className="flex items-center gap-3 min-h-[44px] px-2 rounded-md hover:bg-[#F9FAFB] cursor-pointer text-sm">
              <input type="checkbox" className="w-4 h-4 shrink-0" checked={selected.includes(a)} onChange={() => toggle(a)} />
              <span className="truncate flex-1">{emirateOf[a] ? a.replace(`, ${emirateOf[a]}`, "") : a}</span>
              <span className="text-[#9CA3AF] text-xs shrink-0">{areaCounts[a]}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
